import { useState } from 'react'
import { UploadTask, useUploadStore } from 'hooks/useUploadStore'
import { useAuth } from 'util/auth'
import { InputHeading } from './InputHeading'
import {
  IconFileImport,
  IconFileUpload,
  IconMusic,
  IconVideo,
} from '@tabler/icons-react'
import {
  allowedAudioExtensions,
  allowedVideoExtensions,
} from 'util/validateFileExtension'
import { useRouter } from 'next/router'
import { v4 as uuidv4 } from 'uuid'

export const UploadFile = ({
  projectId,
  onUpload,
}: {
  projectId?: number
  onUpload?: (data: any) => void
}) => {
  const auth = useAuth()
  const router = useRouter()
  const [isDragOver, setIsDragOver] = useState(false)
  const { addUploads } = useUploadStore()

  if (!projectId) {
    projectId = Number(router.query.project_id)
  }

  const handleFileSelect = (files: FileList) => {
    const userId = auth.user ? auth.user.id : ''
    const newUploads: UploadTask[] = Array.from(files).map((file) => ({
      id: uuidv4(),
      name: file.name,
      file,
      progress: 0,
      status: 'pending',
      projectId,
      userId,
      type: 'upload',
    }))

    addUploads(newUploads)
    onUpload?.(newUploads)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    handleFileSelect(files)
  }

  const handleFileChange = (e) => {
    const files = e.target.files
    handleFileSelect(files)
  }

  return (
    <>
      <div className="min-w-full md:flex md:flex-col md:gap-y-2">
        <InputHeading
          icon={<IconFileUpload className="h-6 w-6 text-black" />}
          title="Upload files"
          description="Supports most audio and video file formats."
          expandable={true}
          expandedNode={
            <div className="mt-2 flex flex-col gap-4 rounded-md p-4 pt-2 text-xs text-gray-500">
              <div className="flex flex-col items-start gap-1 text-left">
                <div className="flex gap-1 text-xs font-semibold text-gray-800">
                  <IconVideo className="h-4 w-4" />
                  <span>Video formats:</span>
                </div>
                <p className="text-xs text-gray-500">
                  {allowedVideoExtensions.join(', ')}
                </p>
              </div>
              <div className="flex flex-col items-start gap-1 text-left">
                <div className="flex gap-1 text-xs font-semibold text-gray-800">
                  <IconMusic className="h-4 w-4" />
                  <span>Audio formats:</span>
                </div>
                <p className="text-xs text-gray-500">
                  {allowedAudioExtensions.join(', ')}
                </p>
              </div>
            </div>
          }
        />

        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <div
            className={`flex w-full justify-center rounded-md border-2 ${
              isDragOver
                ? 'border-toasty-blue-600'
                : 'border-dashed border-gray-600'
            } px-6 pb-6 pt-5`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="space-y-1 text-center">
              <IconFileImport className="mx-auto mb-2 h-8 w-8 text-gray-600" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className={`relative cursor-pointer rounded bg-toasty-blue-600 px-1 font-medium text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2`}
                >
                  <span className="text-sm hover:text-gray-200">
                    Select files
                  </span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1 text-gray-600">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-600">Audio or video files only</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
